import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Accordion, AccordionTab } from 'primereact/accordion';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import { apiBase } from '../../utils/config';
import { useCatchResponse } from '../../utils/useCatchResponse';
import moment from 'moment';

const FeeDetailDialog = ({ student, schoolId, financialYear, onProceedToCollect, onClose }) => {
    const { catchResponse } = useCatchResponse();
    const [feeData, setFeeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedAllocations, setSelectedAllocations] = useState([]);

    useEffect(() => {
        fetchFeeDetail();
    }, []);

    const fetchFeeDetail = async () => {
        try {
            setLoading(true);
            const response = await api.post(APIConstant.path.GetStudentFeeDetail, {
                StudentId: student._id,
                SchoolId: schoolId,
                FinancialYearId: financialYear,
            });
            if (response.success) {
                setFeeData(response.data);
            }
            setLoading(false);
            Loader.hide();
        } catch (err) {
            setLoading(false);
            Loader.hide();
            catchResponse(err);
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
    }

    if (!feeData) {
        return <div style={{ textAlign: 'center', padding: '40px' }}>No fee data found</div>;
    }

    const { StudentInfo, Summary, FeeGroups } = feeData;

    return (
        <div className="fee-detail-dialog">
            <div className="fee-student-header" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '16px' }}>
                <div style={{ width: '70px', height: '70px', borderRadius: '50%', overflow: 'hidden', border: '2px solid #1C515E' }}>
                    <img
                        src={StudentInfo.ProfilePic ? `${apiBase}/UserProfilePic/${StudentInfo.ProfilePic}` : ''}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>
                <div>
                    <h3 style={{ margin: 0, color: '#1C515E' }}>{StudentInfo.StudentName}</h3>
                    <p style={{ margin: '4px 0', color: '#666', fontSize: '13px' }}>
                        Class - {StudentInfo.ClassName} | Board - {StudentInfo.BoardName}
                    </p>
                    <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>Roll No. - {StudentInfo.RollNo}</p>
                </div>
                <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto', flexWrap: 'wrap' }}>
                    {[
                        { label: 'Annual Fee', value: Summary.AnnualFee, bg: '#f0f4ff', color: '#1C515E' },
                        { label: 'Fee Paid', value: Summary.FeePaid, bg: '#f0fff4', color: '#2d8a4e' },
                        { label: 'Overdue', value: Summary.Overdue, bg: '#fff5f5', color: '#c53030' },
                        { label: 'Fine', value: Summary.Fine, bg: '#fffbeb', color: '#c05621' },
                        { label: 'Outstanding', value: Summary.Outstanding, bg: '#fff5f5', color: '#c53030' },
                    ].map((card, i) => (
                        <div key={i} style={{ padding: '10px 16px', borderRadius: '8px', background: card.bg, textAlign: 'center', minWidth: '100px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#666' }}>{card.label}</div>
                            <div style={{ fontSize: '16px', fontWeight: 700, color: card.color }}>&#8377; {card.value?.toLocaleString('en-IN') || 0}</div>
                        </div>
                    ))}
                </div>
            </div>

            {FeeGroups?.map((group, gIndex) => (
                <Accordion key={gIndex} className="p-mb-3">
                    <AccordionTab header={
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                            <span>{group.StructureName}</span>
                            <span style={{ fontSize: '13px', color: '#666' }}>Fee Amount: &#8377; {group.TotalAmount?.toLocaleString('en-IN')}</span>
                        </div>
                    }>
                        <DataTable value={group.Allocations} size="small" className="custom-table"
                            selection={selectedAllocations.filter(a => group.Allocations.some(ga => ga._id === a._id))}
                            onSelectionChange={(e) => {
                                const otherGroupSelections = selectedAllocations.filter(a => !group.Allocations.some(ga => ga._id === a._id));
                                const filtered = (e.value || []).filter(a => a.Status !== 'Paid');
                                setSelectedAllocations([...otherGroupSelections, ...filtered]);
                            }}
                            dataKey="_id"
                            selectionMode="checkbox"
                        >
                            <Column selectionMode="multiple" style={{ width: '5%' }} />
                            <Column header="Sr No." body={(_, { rowIndex }) => rowIndex + 1} style={{ width: '8%' }} />
                            <Column field="FeeTypeName" header="Particulars" style={{ width: '18%' }} />
                            <Column header="Due Date" body={(rowData) => rowData.DueDate ? moment(rowData.DueDate).format('DD/MM/YYYY') : ''} style={{ width: '12%' }} />
                            <Column header="Amount" body={(rowData) => `₹ ${rowData.Amount?.toLocaleString('en-IN')}`} style={{ width: '12%' }} />
                            <Column header="Fee Paid" body={(rowData) => `₹ ${rowData.FeePaid?.toLocaleString('en-IN')}`} style={{ width: '12%' }} />
                            <Column header="Overdue" body={(rowData) => `₹ ${rowData.Overdue?.toLocaleString('en-IN')}`} style={{ width: '10%' }} />
                            <Column header="Fine" body={(rowData) => `₹ ${rowData.Fine?.toLocaleString('en-IN')}`} style={{ width: '8%' }} />
                            <Column header="Outstanding" body={(rowData) => `₹ ${rowData.Outstanding?.toLocaleString('en-IN')}`} style={{ width: '12%' }} />
                            <Column header="Status" body={(rowData) => (
                                <Tag value={rowData.Status} severity={rowData.Status === 'Paid' ? 'success' : rowData.Status === 'Partial' ? 'warning' : 'danger'} />
                            )} style={{ width: '10%' }} />
                        </DataTable>
                    </AccordionTab>
                </Accordion>
            ))}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                <Button label="Close" className="p-button-secondary" onClick={onClose} />
                <Button
                    label="Proceed to Collect"
                    className="p-button-primary"
                    disabled={selectedAllocations.length === 0}
                    onClick={() => onProceedToCollect(selectedAllocations)}
                />
            </div>
        </div>
    );
};

export default FeeDetailDialog;
